import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-error-panel>` says about itself.
 *
 * CAOS-579 — every value in every example below is REAL: a code that appears in
 * `kernel/src/errors/catalog.ts`'s `KERNEL_CATALOG`, and a correlation id shaped the way
 * `kernel/src/observability/correlation.ts`'s `mintCorrelationId` actually produces one
 * (`crypto.randomUUID()` — a uuid, never a hand-written `req_…` string). A reader learning
 * this component from its own reference page is taught the shape the platform emits, not a
 * shape it does not.
 *
 * The panel leads with the MESSAGE — the default slot — because that is the one thing every
 * reader can act on without translation; the `code` and `correlation-id` are secondary,
 * mono, receipt-style detail underneath it. `fault` says whose problem it is (`user` /
 * `admin` / `platform`), which is what lets the panel say "fix this", "ask your
 * administrator", or "we're on it" instead of the incumbent's "contact support" — nobody is
 * ever asked to relay the reference to anybody; if the org can decode it, that is what the
 * receipt line under it says.
 */
const docs: ComponentDocs = {
  aliases: ['error state', 'failure screen', 'something went wrong', '500 page', 'crash panel', 'fatal error'],

  answers: [
    'show that a surface failed to load',
    'say whose problem a failure is — something to fix, something for an administrator, or something of ours',
    'let somebody retry in place, when the failure is one that might succeed on a second try',
    'show a failure code and a reference, kept secondary to the message and framed as already recorded',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description:
        'What failed, as a sentence — "This surface failed to load", not "Error". Unset, the heading ' +
        'line is not drawn at all.',
    },
    {
      name: 'eyebrow',
      attribute: 'eyebrow',
      type: 'text',
      description:
        'The small tinted word above the heading, for the class of failure — "Timed out", "Rejected". ' +
        'Set it to an empty string to remove it.',
      default: 'Error',
    },
    {
      name: 'fault',
      attribute: 'fault',
      type: 'text',
      description:
        '`user`, `admin`, or `platform` (the kernel error catalog’s own `Fault` axis). Draws a short, ' +
        'plain-text line under the message saying who can act on the failure — "You can fix this.", ' +
        '"Your administrator can fix this.", or "This one’s on us — we’re already on it." Unset, no ' +
        'line is drawn; the message carries the whole explanation on its own.',
    },
    {
      name: 'retriable',
      attribute: 'retriable',
      type: 'boolean',
      description:
        'Draws a default "Try again" control in the actions row that fires a `retry` event, so a ' +
        'retriable failure can offer one without the caller composing a button. Has no effect once the ' +
        'caller has slotted its own `action` content — one job, one control, never two.',
    },
    {
      name: 'docsHref',
      attribute: 'docs-href',
      type: 'text',
      description:
        'A link to the guide for this failure, drawn in the actions row as "Read the guide". Unset, no ' +
        'link is drawn.',
    },
    {
      name: 'code',
      attribute: 'code',
      type: 'text',
      description:
        'The failure code, drawn in the mono diagnostics block as "Code <value>". A real code is ' +
        'namespaced and stable — `internal.unexpected`, `integration.callout_failed` — never a short ' +
        'opaque one invented for the occasion. Unset, the row is not drawn; with neither code nor ' +
        'correlation id, the whole block collapses.',
    },
    {
      name: 'correlationId',
      attribute: 'correlation-id',
      type: 'text',
      description:
        'The id of the one request that failed, drawn as "Recorded as <value>" with a line underneath ' +
        'saying it is already on file — a RECEIPT, not a token to read aloud or relay onward. A real ' +
        'one is a generated uuid, never a hand-written string.',
    },
  ],

  events: [
    {
      name: 'retry',
      when: 'The default "Try again" control (drawn by `retriable`) is pressed. Retrying is the host’s job.',
      carries: 'nothing',
    },
  ],

  state: [],

  slots: [
    { name: '', takes: 'The message — what this means for the person reading it. The headline; leads the panel.' },
    {
      name: 'action',
      takes:
        'The way forward, hand-composed — a retry, a reload, a way back to somewhere that works. ' +
        'Slotting anything here retracts the default `retriable` control, so a page is never shown two.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The panel fill, which stays neutral rather than tinted.' },
    { token: '--caos-color-border', controls: 'The 1px panel edge, and the edge of the diagnostics block.' },
    { token: '--caos-radius-lg', controls: 'The panel corner radius.' },
    { token: '--caos-color-danger-bg', controls: 'The disc behind the glyph, and the eyebrow fill.' },
    { token: '--caos-color-danger', controls: 'The glyph itself.' },
    { token: '--caos-color-danger-text', controls: 'The eyebrow lettering — the on-tint ink, so it holds up in both themes.' },
    { token: '--caos-radius-pill', controls: 'The round icon disc and the eyebrow.' },
    { token: '--caos-color-text', controls: 'The heading, the fault line, and the labels in the diagnostics block.' },
    { token: '--caos-color-text-muted', controls: 'The message, the diagnostics values, and the receipt sentence.' },
    { token: '--caos-color-surface-2', controls: 'The fill behind the diagnostics block.' },
    { token: '--caos-radius-sm', controls: 'The corners of the diagnostics block and the retry control.' },
    { token: '--caos-color-accent', controls: 'The guide link.' },
    { token: '--caos-color-accent-fill', controls: 'The default retry control’s fill, and --caos-color-accent-fill-hover under the pointer.' },
    { token: '--caos-color-accent-contrast', controls: 'The retry control’s label. White by default; a brand with a pale accent sets a darker ink here.' },
    { token: '--caos-font-display', controls: 'The typeface the heading is set in.' },
    { token: '--caos-font-mono', controls: 'The typeface of the eyebrow and the diagnostics, so a code can be read character by character.' },
    { token: '--caos-space-7', controls: 'The vertical padding of the panel.' },
    { token: '--caos-space-5', controls: 'The horizontal padding of the panel.' },
    { token: '--caos-space-3', controls: 'The gap between the disc, eyebrow, heading, message and diagnostics.' },
    { token: '--caos-space-2', controls: 'The padding inside the diagnostics block and the gap between actions.' },
  ],

  parts: [
    { part: 'panel', is: 'the bordered panel' },
    { part: 'icon', is: 'the danger glyph' },
    { part: 'eyebrow', is: 'the small line above the heading' },
    { part: 'title', is: 'the heading' },
    { part: 'message', is: 'the explanatory text' },
    { part: 'fault', is: 'the "who can act on this" line' },
    { part: 'diagnostics', is: 'the code and correlation-id block' },
    { part: 'actions', is: 'the row holding the retry control, the slotted action, and the guide link' },
    { part: 'retry', is: 'the default "Try again" control drawn by `retriable`' },
    { part: 'guide', is: 'the "Read the guide" link drawn by `docsHref`' },
  ],

  methods: [],

  placing:
    'It stands in place of whatever failed — a section, a tab, a page — rather than above it, because ' +
    'the content it replaces is the content that did not arrive. It centres itself and stops at 460px, ' +
    'so a wide region needs nothing done to it, and a narrow one is not squeezed.',

  whenToUse:
    'A surface that could not be drawn, where there is nothing to show in its place and the failure ' +
    'needs to be written down rather than only announced. Reach for it when the failure is worth a ' +
    'record — set `fault` so the reader knows whose problem it is, and `retriable` when a second try ' +
    'might succeed.',

  insteadUse: [
    {
      name: 'Toast',
      when:
        'The failure is transient and the page still works — a save that will succeed on the next try. ' +
        'A toast is gone before anybody can note anything down, so anything worth finding again later — ' +
        'anything carrying a code or a correlation id — must not be one.',
    },
    {
      name: 'ValidationSummary',
      when:
        'The request was understood and REJECTED because of what was typed. Nothing failed there — ' +
        'every item on that list is something the same person can fix in the next thirty seconds, and a ' +
        'correlation id turns a working form into a support ticket nobody needed to open.',
    },
    {
      name: 'AccessDeniedPanel',
      when:
        'Nothing broke — the caller is not granted it. A refusal has no code to report and will not ' +
        'resolve on a retry, so it needs a grant named and a person to ask, not a reference number.',
    },
    {
      name: 'NoticeBanner',
      when:
        'The surface DID load and something about it is degraded — stale figures, a feed behind, one ' +
        'section unavailable. There is content to sit above, so the banner qualifies it rather than ' +
        'replacing it.',
    },
    {
      name: 'EmptyState',
      when: 'Everything worked and there is genuinely nothing to show.',
    },
  ],

  accessibility:
    'The panel is a `role="alert"` region labelled by its own heading, so a screen reader is told about ' +
    'the failure when the panel arrives rather than when somebody happens to reach it, and the glyph is ' +
    'hidden because the heading already says it. The diagnostics block is ordinary selectable text — ' +
    'the code and the reference can be copied, which a graphic could not offer. Left to the author: the ' +
    'heading has to say what failed (an alert reading "Error" tells a screen-reader user nothing they ' +
    'can act on), and a hand-composed action slot needs a real focusable control, because a person who ' +
    'has just been interrupted by an alert wants somewhere for the caret to go.',

  examples: [
    {
      key: 'platform-fault',
      label: 'A platform failure, with a retry',
      summary:
        'The kernel’s own catch-all (`internal.unexpected`) rendered as this component actually ' +
        'renders it: the message leads, the fault line says it is on us, the retry is drawn by ' +
        '`retriable` rather than hand-composed, and the code + reference sit underneath as a receipt — ' +
        'a real uuid, framed as already recorded rather than as something to relay.',
      placement: {
        component: 'caos-error-panel',
        inputs: {
          heading: 'This surface failed to load',
          fault: 'platform',
          retriable: true,
          code: 'internal.unexpected',
          correlationId: '5c3f8a2e-9b41-4d7a-8e2c-1a6f0d9b3c47',
        },
        children: ["Something went wrong on our end. Reference 5c3f8a2e-9b41-4d7a-8e2c-1a6f0d9b3c47 — we're looking into it."],
      },
      host: {
        summary: 'The page around it owns retrying: it listens for the `retry` event this control raises.',
        run: (stage: HTMLElement) => {
          const panel = stage.querySelector('caos-error-panel');
          if (!panel) return;
          const note = document.createElement('p');
          note.textContent = 'Press "Try again" — what the page was asked to do is reported here.';
          stage.append(note);
          panel.addEventListener('retry', () => {
            note.textContent = 'The page was asked to retry.';
          });
        },
      },
    },
    {
      key: 'way-forward',
      label: 'With a hand-composed way forward',
      summary:
        'A real, currently-emittable integration failure (`integration.callout_failed`, fault `platform`, ' +
        'retriable in the catalog) with its own two controls slotted in — which is why no default retry ' +
        'is drawn: the caller composed its own, and one job gets one control.',
      placement: {
        component: 'caos-error-panel',
        inputs: {
          eyebrow: 'Timed out',
          heading: 'The reporting service did not answer',
          fault: 'platform',
          code: 'integration.callout_failed',
          correlationId: 'd18e4f6b-2a75-4c90-b3e1-7f8a0c5d9e21',
        },
        children: [
          "Couldn't reach the reporting service. We'll retry; no action needed yet.",
          { component: 'caos-button', inputs: { slot: 'action', variant: 'primary' }, children: ['Try again'] },
          { component: 'caos-button', inputs: { slot: 'action', variant: 'ghost' }, children: ['Back to the record'] },
        ],
      },
    },
    {
      key: 'user-fault',
      label: 'A user-fixable failure, with a guide',
      summary:
        'A conflicting write (`conflict.stale_version`, fault `user`) — the reader’s own next action is ' +
        'reload and retry, so `fault="user"` and a link to the guide are shown; nothing here is ' +
        'retriable in place, because the fix is reloading, not pressing the same button again.',
      placement: {
        component: 'caos-error-panel',
        inputs: {
          eyebrow: 'Conflict',
          heading: 'This record changed while you were working',
          fault: 'user',
          code: 'conflict.stale_version',
          correlationId: 'a9024b7e-6f13-4e88-9c5a-3d2b1f0e8a76',
          docsHref: '/guidance/conflicts',
        },
        children: ['Someone else updated this record while you were working. Reload and try again.'],
      },
    },
  ],
};

export default docs;
